import React, { Fragment, useRef, useState, useCallback, useEffect, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { Alarms } from 'common/utils/constants';
import { getMainUnitFromBundleMembers, getMaxAlarmForDevices } from 'common/utils/helpers';
import { isItBundle } from 'common/utils/deviceTwinUtils';
import { deviceClusterIcon } from 'common/deviceMapIcons';
import { updateDevicesPageSelectedDeviceId } from 'redux/actions';
import { mergeRefs } from '../../utils';
import { DeviceMarker } from '../DeviceMarker';
import { DeviceGroupPopup } from '../DeviceGroupPopup';
import { useDeviceMap } from '../DeviceMapContext';
import 'react-leaflet-markercluster/dist/styles.min.css';

const DeviceMarkerGroup = forwardRef(function DeviceMarkerGroup(
  {
    devices,
    mapSelectMode,
    selectedDeviceId,
    selectedDevicesDictionary,
    maxZoom,
    register,
    handleMeasureDeviceOnClick,
    isMeasureModeOn
  },
  ref
) {
  const map = useDeviceMap();
  const bulkViewModalOpen = useSelector((state) => state.ui.devicesPage.bulkViewModalOpen);
  const alarm = useSelector((state) => state.data.alarms?.deviceAlarms != null);
  const mountedRef = useRef(false);
  const clusterRef = useRef(null);
  const [clusterPopup, setClusterPopup] = useState(null);
  const dispatch = useDispatch();
  const createIcon = useCallback(
    (cluster) => {
      const deviceCount = cluster.getChildCount();
      let alarmLevel = null;
      let selected = false;

      if (!bulkViewModalOpen) {
        let bottomCluster = cluster;

        // despite unclear behaviour, using private properties is prohibited
        while (bottomCluster._childClusters.length) {
          bottomCluster = bottomCluster._childClusters[0];
        }

        selected = bottomCluster._zoom === maxZoom && bottomCluster.getChildCount() === deviceCount;
      } else if (alarm) {
        const deviceMarkers = cluster.getAllChildMarkers();
        const clusterDevices = deviceMarkers.map((marker) => marker.options.device);
        const alarmName = getMaxAlarmForDevices(clusterDevices);
        alarmLevel = Alarms[alarmName];
      }

      return deviceClusterIcon({
        deviceCount,
        alarmLevel,
        selected,
        className: ''
      });
      //   @todo check alarms
    },
    [bulkViewModalOpen, maxZoom, alarm]
  );
  const openPopup = useCallback(
    (cluster) => {
      if (map) {
        map.setView(cluster.latlng, maxZoom, { animate: false });
      }

      const clusterDevices = cluster.layer
        .getAllChildMarkers()
        .map(({ options: { device } }) => (isItBundle(device) ? getMainUnitFromBundleMembers(device.members) : device));

      if (isMeasureModeOn) {
        handleMeasureDeviceOnClick(cluster.latlng);
      } else {
        setClusterPopup({
          devices: clusterDevices,
          position: cluster.latlng
        });
      }

      dispatch(updateDevicesPageSelectedDeviceId(null));
    },
    [dispatch, map, maxZoom, isMeasureModeOn, handleMeasureDeviceOnClick]
  );
  const closePopup = useCallback(() => {
    setClusterPopup(null);
  }, []);
  const handlerClick = useCallback(
    (cluster) => {
      /* This piece of code is based on marker cluster source, used to determine if a cluster cannot be zoomed enough to show all children */
      let bottomCluster = cluster.layer;
      while (bottomCluster._childClusters.length === 1) {
        bottomCluster = bottomCluster._childClusters[0];
      }

      if (bottomCluster._zoom === maxZoom && bottomCluster._childCount === cluster.layer._childCount) {
        openPopup(cluster);
      }
    },
    [openPopup, maxZoom]
  );
  const isMounted = useCallback(() => mountedRef.current, []);

  useEffect(() => {
    if (isMounted() && clusterRef.current) {
      const {
        current: { leafletElement }
      } = clusterRef;
      leafletElement.refreshClusters();
    }
  }, [isMounted, bulkViewModalOpen, maxZoom, alarm]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <Fragment>
      <MarkerClusterGroup
        ref={mergeRefs(clusterRef, ref)}
        spiderfyOnMaxZoom={false}
        zoomToBoundsOnClick={true}
        maxClusterRadius={(mapZoom) => (mapZoom >= 15 ? 1 : 20)}
        iconCreateFunction={createIcon}
        onClusterClick={handlerClick}
      >
        {devices.map((device, index) => {
          const selectedId = device.bundleId ?? device.deviceId;

          return (
            <DeviceMarker
              key={selectedId}
              ref={(ref) => register(ref, index)}
              device={device}
              alarm={alarm}
              open={selectedDeviceId === selectedId}
              selected={mapSelectMode.bulk === true && Boolean(selectedDevicesDictionary?.[selectedId])}
              bulkViewModalOpen={bulkViewModalOpen}
              handleMeasureDeviceOnClick={handleMeasureDeviceOnClick}
              isMeasureModeOn={isMeasureModeOn}
            />
          );
        })}
      </MarkerClusterGroup>
      {clusterPopup != null && (
        <DeviceGroupPopup
          devices={clusterPopup.devices}
          position={clusterPopup.position}
          bulkViewModalOpen={bulkViewModalOpen}
          onClose={closePopup}
          handleMeasureDeviceOnClick={handleMeasureDeviceOnClick}
          isMeasureModeOn={isMeasureModeOn}
        />
      )}
    </Fragment>
  );
});

export default DeviceMarkerGroup;
